import { Clickable, Col, Icon, Row, Text } from 'components';
import { useOutsideClick } from 'core';
import { theme } from 'lib';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { filterHistory, getChartData, getDateRange } from 'screens/Dashboard/lib';
import styled from 'styled-components';
import { LineChart, PickerModal } from './';

const options = ['dashboard.week', 'dashboard.month', 'dashboard.year', 'dashboard.all'];

const FilterButton = ({ option, selectedOption, setSelectedOption, onClick, idx, selectedIdx }) => {
  const isSelected = idx === selectedIdx,
    { t } = useTranslation();

  return (
    <Clickable onClick={onClick}>
      <Row
        pad="8px 23px"
        bg={isSelected ? theme.SECONDARY_20 : theme.GREY_10}
        hasRadius="32px"
        hasBorder={`1px solid ${isSelected ? theme.SECONDARY : 'transparent'}`}
      >
        <Text b1 med>
          {t(option)}
        </Text>
      </Row>
    </Clickable>
  );
};

const Wrapper = styled.div`
  height: ${(props) => (props.visible ? '365px' : '0')};
  width: 487px;
  border-radius: 24px;
  position: absolute;
  box-shadow: 0px 0px 16px rgba(0, 0, 0, 0.1);
  background-color: #fff;
  padding: 29px 16px 24px;
  top: 295px;
`;

const ChartModal = ({ visible, closeModal, title, valueTitle1, valueTitle2, type }) => {
  const [chartData, setChartData] = useState(null),
    [labels, setLabels] = useState(null),
    [total, setTotal] = useState(null),
    [filterRange, setFilterRange] = useState(null),
    [showCalendar, setShowCalendar] = useState(false),
    [selectedIdx, setSelectedIdx] = useState(false),
    compRef = useRef(),
    paymentHistory = useSelector((state) => state.user.paymentHistory),
    { t } = useTranslation();

  useOutsideClick(compRef, closeModal, ['date-picker', 'react-datepicker']);

  useEffect(() => {}, [chartData]);
  useEffect(() => {
    if (!visible || !paymentHistory) {
      setChartData(null);
      setLabels(null);
      setTotal(null);
      setFilterRange(null);

      return;
    }

    handleFilterChange(null, 0);
  }, [visible]);

  function handleCalendar() {
    setShowCalendar(!showCalendar);
  }

  function handleFilterChange(values, idx) {
    setSelectedIdx(idx);
    setChartData(null);
    const newRange = getDateRange(values, idx);

    const filtered = filterHistory(paymentHistory, newRange.range, !!newRange.all, type);

    setFilterRange(newRange);

    const generatedData = getChartData(filtered, newRange.range),
      newLabels = generatedData.map((x) => x.label),
      newData = generatedData.map((x) => Number(x.amount));

    setTotal({
      amount: newData.reduce((x, y) => x + y, 0),
      count: filtered.length
    });

    setLabels(newLabels);
    setChartData(newData);
  }

  if (!visible) return null;

  return (
    <Wrapper ref={compRef} visible={visible}>
      <Row noFlex between center>
        <Text h3>{title}</Text>
      </Row>

      <Row noFlex marg="12px 0 0">
        <Col noFlex marg="0 48px 0 0">
          <Text>{valueTitle1}</Text>

          <Text h1 marg="4px 0 0">
            {total?.amount.toLocaleString(undefined, {
              style: 'currency',
              currency: 'USD'
            })}
          </Text>
        </Col>

        <Col noFlex>
          <Text>{valueTitle2}</Text>

          <Text h1 marg="4px 0 0">
            {total?.count}
          </Text>
        </Col>
      </Row>

      <Row noFlex marg="12px 0 0" between>
        {options.map((x, i) => (
          <FilterButton
            option={x}
            idx={i}
            selectedIdx={selectedIdx}
            selectedOption={filterRange?.filter}
            key={i}
            onClick={() => handleFilterChange(null, i)}
          />
        ))}

        <FilterButton
          option={t('dashboard.custom')}
          onClick={handleCalendar}
          idx={4}
          selectedIdx={selectedIdx}
          selectedOption={filterRange?.filter}
        />
      </Row>

      {showCalendar && (
        <PickerModal handleClose={handleCalendar} handleFilterChange={handleFilterChange} isChart />
      )}

      {chartData && (
        <Col ht="175px" marg="24px 0 0">
          <LineChart data={chartData} labels={labels} filter={filterRange?.filter} />
        </Col>
      )}

      <Col pos="absolute" right="21px" top="21px">
        <Clickable onClick={closeModal}>
          <Icon type="close" dimensions={14} col={theme.GREY_60} />
        </Clickable>
      </Col>
    </Wrapper>
  );
};

export default ChartModal;
